import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    TrendingUp,
    ShoppingBag,
    ArrowUpRight,
    ArrowDownRight,

} from 'lucide-react';
import api from '../../services/api';
import { useUser } from '../../context/UserContext';

const AdminDashboard = () => {
    const { user } = useUser();
    const navigate = useNavigate();
    const [stats, setStats] = useState({
        visits: 0,
        whatsappClicks: 0,
        todayWhatsapp: 0,
        weekWhatsapp: 0,
        monthWhatsapp: 0,
        totalViews: 0,
        totalProducts: 0
    });
    const [loading, setLoading] = useState(true);

    const [mostViewedProducts, setMostViewedProducts] = useState([]);

    useEffect(() => {
        const fetchDashboardData = async () => {
            try {
                setLoading(true);
                // Fetch products and analytics in parallel
                const [productsRes, analyticsRes] = await Promise.all([
                    api.get('/api/products/'),
                    api.get('/api/admin/stats/').catch(() => ({ data: { total_visits: 0, total_whatsapp_clicks: 0, today_whatsapp: 0, week_whatsapp: 0, month_whatsapp: 0 } }))
                ]);

                // Calculate stats
                const totalViews = productsRes.data.reduce((acc, p) => acc + (p.views_count || 0), 0);

                setStats({
                    visits: analyticsRes.data.total_visits || 0,
                    whatsappClicks: analyticsRes.data.total_whatsapp_clicks || 0,
                    todayWhatsapp: analyticsRes.data.today_whatsapp || 0,
                    weekWhatsapp: analyticsRes.data.week_whatsapp || 0,
                    monthWhatsapp: analyticsRes.data.month_whatsapp || 0,
                    totalViews,
                    totalProducts: productsRes.data.length
                });

                // Get top 5 most viewed products
                const sortedProducts = [...productsRes.data].sort((a, b) =>
                    (b.views_count || 0) - (a.views_count || 0)
                );
                setMostViewedProducts(sortedProducts.slice(0, 5));

                setLoading(false);
            } catch (err) {
                console.error('Error fetching dashboard data:', err);
                setLoading(false);
            }
        };
        fetchDashboardData();
    }, []);

    const widgets = [
        {
            label: 'Visites du Site',
            value: stats.visits.toLocaleString('fr-FR'),
            icon: ArrowUpRight,
            color: 'bg-emerald-500',
            trend: 'Direct',
            isUp: true
        },
        {
            label: 'WhatsApp ',
            value: stats.monthWhatsapp.toLocaleString('fr-FR'),
            icon: ShoppingBag,
            color: 'bg-green-600',
            trend: 'Recette',
            isUp: true
        },

        {
            label: 'Total Produits',
            value: stats.totalProducts.toLocaleString('fr-FR'),
            icon: TrendingUp,
            color: 'bg-brand',
            trend: 'Stable',
            isUp: true
        },
    ];

    if (loading) return (
        <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brand"></div>
        </div>
    );

    return (
        <div className="space-y-10">
            {/* Header Title */}
            <div>
                <h1 className="text-3xl font-black text-green-950 mb-2">Tableau de bord</h1>
                <p className="text-slate-500">Bienvenue, <span className="text-brand font-bold">{user?.full_name || user?.name || user?.username || 'Administrateur'}</span>, sur votre espace d'administration.</p>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {widgets.map((widget, i) => (
                    <div key={i} className="bg-white p-8 rounded-[2.5rem] border border-slate-100 shadow-sm hover:shadow-xl hover:shadow-slate-200/50 transition-all group">
                        <div className="flex justify-between items-start mb-6">
                            <div className={`w-14 h-14 ${widget.color} rounded-2xl flex items-center justify-center text-white shadow-lg shadow-${widget.color.split('-')[1]}-200 transition-transform group-hover:scale-110`}>
                                <widget.icon className="w-6 h-6" />
                            </div>
                            <div className={`flex items-center gap-1 text-sm font-bold ${widget.isUp ? 'text-emerald-500' : 'text-red-500'}`}>
                                {widget.trend}
                                {widget.isUp ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                            </div>
                        </div>
                        <p className="text-slate-500 font-bold text-sm uppercase  mb-1">{widget.label}</p>
                        <h3 className="text-2xl font-black text-green-950">{widget.value}</h3>
                    </div>
                ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
                {/* Most Viewed Products */}
                <div className="lg:col-span-2 bg-white rounded-[2.5rem] border border-slate-100 shadow-sm p-8">
                    <div className="flex items-center justify-between mb-8">
                        <h3 className="text-xl font-black text-green-950">Produits les plus visités</h3>
                        <button
                            onClick={() => navigate('/admin/inventory')}
                            className="text-brand font-bold text-sm hover:underline transition-all hover:text-green-700"
                        >
                            Voir tout l'inventaire
                        </button>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="text-left py-4 border-b border-slate-100">
                                    <th className="pb-4 text-xs font-black text-slate-400 uppercase tracking-widest">Produit</th>
                                    <th className="pb-4 text-xs font-black text-slate-400 uppercase tracking-widest">Catégorie</th>
                                    <th className="pb-4 text-xs font-black text-slate-400 uppercase tracking-widest">Prix</th>
                                    <th className="pb-4 text-xs font-black text-slate-400 uppercase tracking-widest">Visites</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-50">
                                {mostViewedProducts.length > 0 ? (
                                    mostViewedProducts.map((product) => (
                                        <tr key={product.id} className="group hover:bg-slate-50/50 transition-all">
                                            <td className="py-5">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 rounded-xl bg-slate-100 overflow-hidden shrink-0">
                                                        <img
                                                            src={product.image.startsWith('http') ? product.image : `${api.defaults.baseURL}${product.image}`}
                                                            alt={product.name}
                                                            className="w-full h-full object-cover"
                                                        />
                                                    </div>
                                                    <div>
                                                        <p className="font-bold text-green-950 text-sm">{product.name}</p>
                                                        <p className="text-[10px] text-slate-400 uppercase">
                                                            Stock: {product.stock}
                                                        </p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="py-5">
                                                <p className="text-sm font-medium text-slate-600">
                                                    {product.category_name || 'Général'}
                                                </p>
                                            </td>
                                            <td className="py-5">
                                                <p className="font-bold text-green-950 text-sm">
                                                    {Math.floor(product.price).toLocaleString('fr-FR')} Fcfa
                                                </p>
                                            </td>
                                            <td className="py-5">
                                                <div className="flex items-center gap-2">
                                                    <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center text-emerald-600">
                                                        <TrendingUp className="w-4 h-4" />
                                                    </div>
                                                    <span className="font-black text-green-950 text-sm">
                                                        {product.views_count || 0}
                                                    </span>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="4" className="py-10 text-center text-slate-400 font-medium">
                                            Aucun produit consulté
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Performance Brief */}
                <div className="bg-green-950 rounded-[2.5rem] text-white p-10 shadow-2xl relative overflow-hidden">
                    <div className="relative z-10">
                        <h3 className="text-xl font-bold mb-8">Performance du jour</h3>
                        <div className="space-y-8">
                            <div className="flex items-center justify-between">
                                <div className="space-y-1">
                                    <p className="text-white/60 text-sm font-medium">Clics WhatsApp (Total)</p>
                                    <h4 className="text-2xl font-black tracking-tight">{stats.whatsappClicks.toLocaleString('fr-FR')} clics</h4>
                                </div>
                                <div className="p-3 bg-white/10 rounded-2xl">
                                    <TrendingUp className="w-6 h-6 text-brand" />
                                </div>
                            </div>

                            <div className="pt-8 border-t border-white/10 space-y-4">
                                <div className="flex justify-between text-sm">
                                    <span className="text-white/60">Engagement</span>
                                    <span className="font-bold">Actif</span>
                                </div>
                                <div className="h-3 bg-white/10 rounded-full overflow-hidden">
                                    <div className="h-full bg-brand rounded-full" style={{ width: '100%' }}></div>
                                </div>
                            </div>

                            <button
                                onClick={() => {
                                    const reportData = [
                                        { metric: 'Visites Totales du Site', value: stats.visits },
                                        { metric: 'Clics WhatsApp (Total)', value: stats.whatsappClicks },
                                        { metric: 'Total Articles Visités (Cumul)', value: stats.totalViews },
                                        { metric: 'Articles en Stock', value: stats.totalProducts },
                                        { metric: '---', value: '---' },
                                        { metric: 'TOP PRODUITS VISITÉS', value: '' },
                                        ...mostViewedProducts.map(p => ({
                                            metric: p.name,
                                            value: `${p.views_count} fois visité | ${p.category_name || 'Général'} | ${Math.floor(p.price).toLocaleString('fr-FR')} Fcfa`
                                        }))
                                    ];
                                    const headers = [
                                        { label: 'Indicateur / Produit', key: 'metric' },
                                        { label: 'Valeur / Détails', key: 'value' }
                                    ];
                                    import('../../utils/exportUtils').then(utils => {
                                        utils.downloadCSV(reportData, `resume_dashboard_${new Date().toISOString().split('T')[0]}`, headers);
                                    });
                                }}
                                className="w-full h-14 bg-white text-green-950 rounded-2xl font-black text-sm hover:bg-brand hover:text-white transition-all mt-6 shadow-xl shadow-black/20"
                            >
                                Télécharger le rapport
                            </button>
                        </div>
                    </div>
                    {/* Decorative element */}
                    <div className="absolute top-0 right-0 w-32 h-32 bg-brand/10 rounded-full blur-3xl -mr-16 -mt-16"></div>
                </div>
            </div>
        </div>
    );
};


export default AdminDashboard;
